import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceDot,
} from "recharts";
import { TrendingUp, AlertTriangle } from "lucide-react";

export default function BurnRateChart({ missionId, compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!missionId) return;
    api.get(`/missions/${missionId}/burn-rate`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [missionId]);

  if (!data) return null;
  if (!data.series || data.series.length === 0) {
    return (
      <div className="text-xs text-zinc-500 italic py-3">No consumption yet — nothing to project.</div>
    );
  }

  // Build chart points: real series + projection to end_date
  const chartData = data.series.map((p) => ({ date: p.date, actual: p.cumulative_jh }));
  if (data.projection_end_jh != null && data.end_date) {
    const last = data.series[data.series.length - 1];
    chartData.push({ date: last.date, actual: last.cumulative_jh, projection: last.cumulative_jh });
    chartData.push({ date: data.end_date, projection: data.projection_end_jh });
  }

  const overBudgetProjected = data.projection_end_jh != null && data.budget_jh > 0 && data.projection_end_jh > data.budget_jh;
  const budgetYaxisMax = Math.max(data.budget_jh || 0, data.projection_end_jh || 0, (data.series.slice(-1)[0]?.cumulative_jh || 0)) * 1.1;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="burn-rate-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp size={15} className="text-zinc-700" />
          <h3 className="font-heading font-semibold text-sm uppercase tracking-wider">Burn-rate & projection</h3>
        </div>
        {overBudgetProjected && (
          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-red-100 text-red-800 rounded-full font-mono" data-testid="burn-rate-over-budget">
            <AlertTriangle size={10} /> PROJECTED OVERRUN
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={compact ? 180 : 250}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={20} />
          <YAxis tick={{ fontSize: 10 }} domain={[0, budgetYaxisMax]} />
          <Tooltip formatter={(v, n) => [v != null ? `${v} JH` : "—", n === "actual" ? "Consumed" : "Projection"]} />
          {data.budget_jh > 0 && (
            <ReferenceLine y={data.budget_jh} stroke="#DC2626" strokeDasharray="4 2" label={{ value: `Budget ${data.budget_jh} JH`, fontSize: 9, fill: "#DC2626", position: "insideTopRight" }} />
          )}
          <Area type="monotone" dataKey="actual" stroke="#2563EB" fill="#2563EB" fillOpacity={0.15} strokeWidth={2} />
          <Line type="monotone" dataKey="projection" stroke={overBudgetProjected ? "#DC2626" : "#16A34A"} strokeWidth={2} strokeDasharray="6 3" dot={false} />
          {data.projection_crosses_budget && (
            <ReferenceDot x={data.projection_crosses_budget} y={data.budget_jh} r={5} fill="#DC2626" stroke="#fff" strokeWidth={2} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="mt-3 grid grid-cols-3 gap-3 text-xs font-mono text-zinc-600">
        <div><span className="text-zinc-400">Burn/day:</span> <span className="text-zinc-900">{data.burn_per_day != null ? `${data.burn_per_day} JH` : "—"}</span></div>
        <div><span className="text-zinc-400">Projection:</span> <span className={overBudgetProjected ? "text-red-700 font-semibold" : "text-zinc-900"}>{data.projection_end_jh != null ? `${data.projection_end_jh} JH` : "—"}</span></div>
        <div><span className="text-zinc-400">Budget hit:</span> <span className="text-zinc-900">{data.projection_crosses_budget || "—"}</span></div>
      </div>
    </div>
  );
}
